import * as reduxApi from 'src/utils/redux-api-v2';
import type {Dispatch, ThunkAction, GetState} from 'src/reducers';
import {ANALYTICS_API_V4} from 'src/utils/analytics-api-migration';
import type {ApiOptions, ApiPostOptions} from 'src/utils/redux-api';
import type {
  SelectedEntity,
  CurrentFilters,
  SavedDashboardList,
  SelectedSurveyNpsFilterColumnsData,
  SelectedQuestionFilters,
  SelectedNpsChartFilterOptionsData,
} from 'src/types/analytics-genesis';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import {generateUUID} from 'src/utils/page-site';

import {thunkify as flow} from 'src/utils/thunks';
import {isEqual} from 'lodash';
import {fetching, key} from 'src/utils/redux';
import {
  npsClientTableColumnsQuery,
  npsCandidateTableColumnsQuery,
  conversionSummaryTableColumnsQuery,
  allSurveysTableColumnsQuery,
  npsCandidateChartColumnsQuery,
  npsClientChartColumnsQuery,
  allResponsesTableColumnsQuery,
  transformEntities,
} from 'src/utils/analytics-genesis';


const analyticsOptions: ApiOptions = {apiPath: ANALYTICS_API_V4};
const analyticsApiOptions: ApiPostOptions = {apiPath: ANALYTICS_API_V4};

export const SET_CURRENT_DASHBOARD_ID =
  'analytics-survey-and-nps/set-current-dashboard';
export const SET_SELECTED_ENTITIES_FILTER =
  'analytics-survey-and-nps/set-selected-entities-filter';
export const SET_SELECTED_JOURNEY_FILTERS =
  'analytics-survey-and-nps/set-selected-journey-filters';
export const SET_SELECTED_DATE_RANGE =
  'analytics-survey-and-nps/set-selected-date-range';
export const SET_SELECTED_DATE_RANGE_DRILLDOWN =
  'analytics-survey-and-nps/set-selected-date-range-drilldown';
export const CLEAR_SELECTED_FILTERS =
  'analytics-survey-and-nps/clear-selected-filters';
export const RECEIVE_SAVED_DASHBOARDS =
  'analytics-survey-and-nps/receive-saved-dashboards';
export const RECEIVE_SAVED_DRILLDOWN_DASHBOARDS =
  'analytics-survey-and-nps/receive-saved-drilldown-dashboards';
export const RECEIVE_CANDIDATE_NPS_TABLE_COLUMNS =
  'analytics-survey-and-nps/receive-candidate-nps-table-columns';
export const RECEIVE_CLIENT_NPS_TABLE_COLUMNS =
  'analytics-survey-and-nps/receive-client-nps-table-columns';
export const RECEIVE_ALL_SURVEYS_TABLE_COLUMNS =
  'analytics-survey-and-nps/receive-all-surveys-table-columns';
export const RECEIVE_CONVERSION_SUMMARY_TABLE_COLUMNS =
  'analytics-survey-and-nps/receive-conversion-summary-table-columns';

export const CLEAR_DRILLDOWN_SELECTED_FILTERS =
  'analytics-survey-and-nps/clear_drilldown-selected-filters';
export const SET_SURVEY_DRILLDOWN_DASHBOARD_ID =
  'analytics-survey-and-nps/set-survey-drilldown-dashboard';
export const SET_SELECTED_QUESTIONS_FILTER_SURVEY_DRILLDOWN =
  'analytics-survey-and-nps/set-selected-questions-filter-survey-drilldown';
export const SET_SELECTED_JOURNEY_FILTERS_SURVEY_DRILLDOWN =
  'analytics-survey-and-nps/set-selected-journey-filters-survey-drilldown';
export const SET_SELECTED_ENTITIES_FILTER_SURVEY_DRILLDOWN =
  'analytics-survey-and-nps/set-selected-entities-filter-survey-drilldown';
export const RECEIVE_CANDIDATE_NPS_CHART_COLUMNS =
  'analytics-survey-and-nps/receive-candidate-nps-chart-columns';
export const RECEIVE_CLIENT_NPS_CHART_COLUMNS =
  'analytics-survey-and-nps/receive-client-nps-chart-columns';
export const RECEIVE_ALL_RESPONSES_TABLE_COLUMNS =
  'analytics-survey-and-nps/receive-all-responses-table-columns';
export const RECEIVE_SENT_FROM_OPTIONS =
  'analytics-survey-and-nps/receive-sent-from-options';
export const SET_NAVIGATION_DOWN =
  'analytics-survey-and-nps/set-navigation-down';
export const SET_NAVIGATION_UP = 'analytics-survey-and-nps/set-navigation-up';

type SetCurrentDashboardIdAction = {
  type: typeof SET_CURRENT_DASHBOARD_ID;
  payload: string | null | undefined;
};

type SetSurveyDrilldownDashboardIdAction = {
  type: typeof SET_SURVEY_DRILLDOWN_DASHBOARD_ID;
  payload: string | null | undefined;
};

type SetSelectedEntitiesFiltersAction = {
  type: typeof SET_SELECTED_ENTITIES_FILTER;
  payload: Array<SelectedEntity>;
};

type SetSelectedEntitiesFiltersSurveyDrilldownAction = {
  type: typeof SET_SELECTED_ENTITIES_FILTER_SURVEY_DRILLDOWN;
  payload: Array<SelectedEntity>;
};

type SetSelectedQuestionsFiltersSurveyDrilldownAction = {
  type: typeof SET_SELECTED_QUESTIONS_FILTER_SURVEY_DRILLDOWN;
  payload: Array<SelectedQuestionFilters>;
};

type SetSelectedJourneyFiltersAction = {
  type: typeof SET_SELECTED_JOURNEY_FILTERS;
  payload: CurrentFilters;
};

type SetSelectedJourneyFiltersSurveyDrilldownAction = {
  type: typeof SET_SELECTED_JOURNEY_FILTERS_SURVEY_DRILLDOWN;
  payload: CurrentFilters;
};

type SetDateRangeAction = {
  type: typeof SET_SELECTED_DATE_RANGE;
  payload: DateRangeWithTimezone;
};

type SetDateRangeDrilldownAction = {
  type: typeof SET_SELECTED_DATE_RANGE_DRILLDOWN;
  payload: DateRangeWithTimezone;
};

type ReceiveSavedDashboardsAction = {
  type: typeof RECEIVE_SAVED_DASHBOARDS;
  payload: SavedDashboardList;
};

type ReceiveSavedDrilldownDashboardsAction = {
  type: typeof RECEIVE_SAVED_DRILLDOWN_DASHBOARDS;
  payload: SavedDashboardList;
};

type ClearSelectedFiltersAction = {
  type: typeof CLEAR_SELECTED_FILTERS;
};

type ClearDrilldownSelectedFiltersAction = {
  type: 'analytics-survey-and-nps/clear_drilldown-selected-filters';
};

type ReceiveCandidateNPSTableColumnAction = {
  type: typeof RECEIVE_CANDIDATE_NPS_TABLE_COLUMNS;
  payload: SelectedSurveyNpsFilterColumnsData;
};

type ReceiveCandidateNPSChartColumnAction = {
  type: typeof RECEIVE_CANDIDATE_NPS_CHART_COLUMNS;
  payload: Array<string>;
};

type ReceiveClientNPSChartColumnAction = {
  type: typeof RECEIVE_CLIENT_NPS_CHART_COLUMNS;
  payload: Array<string>;
};

type ReceiveClientNPSTableColumnAction = {
  type: typeof RECEIVE_CLIENT_NPS_TABLE_COLUMNS;
  payload: SelectedSurveyNpsFilterColumnsData;
};

type ReceiveAllSurveysTableColumnAction = {
  type: typeof RECEIVE_ALL_SURVEYS_TABLE_COLUMNS;
  payload: SelectedSurveyNpsFilterColumnsData;
};

type ReceiveConversionSummaryTableColumnAction = {
  type: typeof RECEIVE_CONVERSION_SUMMARY_TABLE_COLUMNS;
  payload: SelectedSurveyNpsFilterColumnsData;
};

type ReceiveAllResponsesTableColumnAction = {
  type: typeof RECEIVE_ALL_RESPONSES_TABLE_COLUMNS;
  payload: SelectedSurveyNpsFilterColumnsData;
};

type ReceiveSentFromOptionsAction = {
  type: typeof RECEIVE_SENT_FROM_OPTIONS;
  payload: Array<string>;
};

type SetNavigationDownAction = {
  type: typeof SET_NAVIGATION_DOWN;
  payload: boolean;
};

type SetNavigationUpAction = {
  type: typeof SET_NAVIGATION_UP;
  payload: boolean;
};

export type AnalyticsSurveyAndNPSActions =
  | SetSelectedEntitiesFiltersAction
  | SetSelectedJourneyFiltersAction
  | ReceiveSavedDashboardsAction
  | ReceiveSavedDrilldownDashboardsAction
  | SetCurrentDashboardIdAction
  | ClearSelectedFiltersAction
  | SetDateRangeAction
  | SetDateRangeDrilldownAction
  | ReceiveCandidateNPSTableColumnAction
  | ReceiveClientNPSTableColumnAction
  | ReceiveAllSurveysTableColumnAction
  | ReceiveConversionSummaryTableColumnAction
  | ClearDrilldownSelectedFiltersAction
  | SetSurveyDrilldownDashboardIdAction
  | SetSelectedQuestionsFiltersSurveyDrilldownAction
  | SetSelectedEntitiesFiltersSurveyDrilldownAction
  | SetSelectedJourneyFiltersSurveyDrilldownAction
  | ReceiveCandidateNPSChartColumnAction
  | ReceiveAllResponsesTableColumnAction
  | ReceiveSentFromOptionsAction
  | SetNavigationDownAction
  | SetNavigationUpAction;

export const setCurrentDashboardId = (
  payload: string,
): SetCurrentDashboardIdAction => ({
  type: SET_CURRENT_DASHBOARD_ID,
  payload,
});

export const setSurveyDrilldownDashboardId = (
  payload: string,
): SetSurveyDrilldownDashboardIdAction => ({
  type: SET_SURVEY_DRILLDOWN_DASHBOARD_ID,
  payload,
});

export const setSelectedEntitiesFilter = (
  payload: Array<SelectedEntity>,
): SetSelectedEntitiesFiltersAction => ({
  type: SET_SELECTED_ENTITIES_FILTER,
  payload,
});

export const setSelectedEntitiesFiltersSurveyDrilldown = (
  payload: Array<SelectedEntity>,
): SetSelectedEntitiesFiltersSurveyDrilldownAction => ({
  type: SET_SELECTED_ENTITIES_FILTER_SURVEY_DRILLDOWN,
  payload,
});

export const setSelectedQuestionsFilterSurveyDrilldown = (
  payload: Array<SelectedQuestionFilters>,
): SetSelectedQuestionsFiltersSurveyDrilldownAction => ({
  type: SET_SELECTED_QUESTIONS_FILTER_SURVEY_DRILLDOWN,
  payload,
});

export const setSelectedJourneyFilters = (
  payload: CurrentFilters,
): SetSelectedJourneyFiltersAction => ({
  type: SET_SELECTED_JOURNEY_FILTERS,
  payload,
});

export const setSelectedJourneyFiltersSurveyDrilldown = (
  payload: CurrentFilters,
): SetSelectedJourneyFiltersAction => ({
  // @ts-expect-error - TS2322 - Type '"analytics-survey-and-nps/set-selected-journey-filters-survey-drilldown"' is not assignable to type '"analytics-survey-and-nps/set-selected-journey-filters"'.
  type: SET_SELECTED_JOURNEY_FILTERS_SURVEY_DRILLDOWN,
  payload,
});

export const setDateRange = (
  payload: DateRangeWithTimezone,
): SetDateRangeAction => ({
  type: SET_SELECTED_DATE_RANGE,
  payload,
});

export const setDateRangeDrilldown = (
  payload: DateRangeWithTimezone,
): SetDateRangeDrilldownAction => ({
  type: SET_SELECTED_DATE_RANGE_DRILLDOWN,
  payload,
});

export const receiveSavedDashboards = (
  payload: SavedDashboardList,
): ReceiveSavedDashboardsAction => ({
  type: RECEIVE_SAVED_DASHBOARDS,
  payload,
});

export const receiveSavedDrilldownDashboards = (
  payload: SavedDashboardList,
): ReceiveSavedDrilldownDashboardsAction => ({
  type: RECEIVE_SAVED_DRILLDOWN_DASHBOARDS,
  payload,
});

export const clearSelectedFilters = (): ClearSelectedFiltersAction => ({
  type: CLEAR_SELECTED_FILTERS,
});

export const clearDrilldownSelectedFilters =
  (): ClearDrilldownSelectedFiltersAction => ({
    type: CLEAR_DRILLDOWN_SELECTED_FILTERS,
  });

const receiveCandidateNPSTableColumnSelection = (
  payload: SelectedSurveyNpsFilterColumnsData,
): ReceiveCandidateNPSTableColumnAction => ({
  type: RECEIVE_CANDIDATE_NPS_TABLE_COLUMNS,
  payload,
});

const receiveCandidateNPSChartColumnSelection = (
  payload: Array<string>,
): ReceiveCandidateNPSChartColumnAction => ({
  type: RECEIVE_CANDIDATE_NPS_CHART_COLUMNS,
  payload,
});

const receiveClientNPSChartColumnSelection = (
  payload: Array<string>,
): ReceiveClientNPSChartColumnAction => ({
  type: RECEIVE_CLIENT_NPS_CHART_COLUMNS,
  payload,
});

const receiveClientNPSTableColumnSelection = (
  payload: SelectedSurveyNpsFilterColumnsData,
): ReceiveClientNPSTableColumnAction => ({
  type: RECEIVE_CLIENT_NPS_TABLE_COLUMNS,
  payload,
});

const receiveAllSurveysTableColumnSelection = (
  payload: SelectedSurveyNpsFilterColumnsData,
): ReceiveAllSurveysTableColumnAction => ({
  type: RECEIVE_ALL_SURVEYS_TABLE_COLUMNS,
  payload,
});

const receiveConversionSummaryTableColumnSelection = (
  payload: SelectedSurveyNpsFilterColumnsData,
): ReceiveConversionSummaryTableColumnAction => ({
  type: RECEIVE_CONVERSION_SUMMARY_TABLE_COLUMNS,
  payload,
});

const receiveAllResponsesTableColumnSelection = (
  payload: SelectedSurveyNpsFilterColumnsData,
): ReceiveAllResponsesTableColumnAction => ({
  type: RECEIVE_ALL_RESPONSES_TABLE_COLUMNS,
  payload,
});

const receiveSentFromOptions = (
  payload: Array<string>,
): ReceiveSentFromOptionsAction => ({
  type: RECEIVE_SENT_FROM_OPTIONS,
  payload,
});

export const setNavigationDown = (
  payload: boolean,
): SetNavigationDownAction => ({
  type: SET_NAVIGATION_DOWN,
  payload,
});

export const setNavigationUp = (payload: boolean): SetNavigationUpAction => ({
  type: SET_NAVIGATION_UP,
  payload,
});

export const getSavedNPSAndSurveyDashboards: () => ThunkAction<unknown> =
  () => async (dispatch: Dispatch, getState: GetState) => {
    try {
      const response = await dispatch(
        reduxApi.get(
          '/candidate-nps/save-filter',
          {
            product: 'nps-and-survey',
            dashboard: 'nps-and-surveys',
          },
          {apiPath: ANALYTICS_API_V4},
        ),
      );
      const {
        filters,
      }: {
        filters: SavedDashboardList;
      } = response;
      await dispatch(receiveSavedDashboards(filters));
      const selectedJourneyFilters =
        getState().analyticsSurveyAndNPS.selectedJourneyFilters;
      await dispatch(
        setSelectedJourneyFilters({
          ...selectedJourneyFilters,
          channels: ['email', 'sms'],
        }),
      );

      const defaultDashboard = filters.find(({is_default}) => is_default);
      if (defaultDashboard) {
        const {id = '', filters = {}} = defaultDashboard;
        const {
          // @ts-expect-error - TS2339 - Property 'journeys' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          journeys = [],
          // @ts-expect-error - TS2339 - Property 'journey_status' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          journey_status = [],
          // @ts-expect-error - TS2339 - Property 'journey_category' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          journey_category = [],
          // @ts-expect-error - TS2339 - Property 'journey_business_goal' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          journey_business_goal = [],
          // @ts-expect-error - TS2339 - Property 'journey_lifecycle_goal' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          journey_lifecycle_goal = [],
          // @ts-expect-error - TS2339 - Property 'question_types' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          question_types = [],
          // @ts-expect-error - TS2339 - Property 'channels' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          channels = [],
          // @ts-expect-error - TS2339 - Property 'from_address' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          from_address = [],
          // @ts-expect-error - TS2339 - Property 'entity_data_filter' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          entity_data_filter = [{id: generateUUID()}],
          // @ts-expect-error - TS2339 - Property 'selected_date_range' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          selected_date_range = {
            startDateUTC: null,
            startDate: null,
            endDateUTC: null,
            endDate: null,
          },
        } = filters;
        await dispatch(setCurrentDashboardId(id));
        await dispatch(setSelectedEntitiesFilter(entity_data_filter));
        await dispatch(
          setSelectedJourneyFilters({
            journeys,
            journey_status,
            journey_category,
            journey_business_goal,
            journey_lifecycle_goal,
            question_types,
            channels,
            from_address,
          }),
        );
        await dispatch(setDateRange(selected_date_range));
      }
    } catch (error: any) {
      console.error('Error fetching saved dashboard filter:', error);
    } finally {
      dispatch(setNavigationUp(false));
      dispatch(setNavigationDown(false));
    }
  };

export const getSavedSurveyDrillDashboards: () => ThunkAction<unknown> =
  () => async (dispatch: Dispatch, getState: GetState) => {
    try {
      const response = await dispatch(
        reduxApi.get(
          '/all-surveys/save-filter',
          {
            product: 'nps-and-survey',
            dashboard: 'survey-conversion-summary',
          },
          {apiPath: ANALYTICS_API_V4},
        ),
      );
      const {
        filters,
      }: {
        filters: SavedDashboardList;
      } = response;
      await dispatch(receiveSavedDrilldownDashboards(filters));
      const isNavigationDown =
        getState().analyticsSurveyAndNPS.isNavigationDown;
      const parentEntitiesFilters =
        getState().analyticsSurveyAndNPS.selectedEntitiesFilters;
      const parentJourneyFilters =
        getState().analyticsSurveyAndNPS.selectedJourneyFilters;

      const parentDateRange =
        getState().analyticsSurveyAndNPS.selectedDateRange;

      const savedParentDashboards =
        getState().analyticsSurveyAndNPS.savedDashboards ?? [];

      const selectedParentDashboardId =
        getState().analyticsSurveyAndNPS.currentDashboardID;

      const parentDashboard = savedParentDashboards.find(
        ({id}) => id === selectedParentDashboardId,
      );

      const {filters: parentFilters = {}} = parentDashboard ?? {};

      const {
        //@ts-expect-error
        entity_data_filter: entityFilters = [{id: generateUUID()}],
        //@ts-expect-error
        selected_date_range = {
          startDateUTC: null,
          startDate: null,
          endDateUTC: null,
          endDate: null,
        },
        ...journeyFilters
      } = parentFilters ?? {};

      const loadSavedFilters = async () => {
        const defaultDashboard = filters.find(({is_default}) => is_default);
        if (defaultDashboard) {
          const {id = '', filters = {}} = defaultDashboard;
          const {
            // @ts-expect-error - TS2339 - Property 'journeys' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
            journeys = [],
            // @ts-expect-error - TS2339 - Property 'journey_status' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
            journey_status = [],
            // @ts-expect-error - TS2339 - Property 'journey_category' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
            journey_category = [],
            // @ts-expect-error - TS2339 - Property 'journey_business_goal' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
            journey_business_goal = [],
            // @ts-expect-error - TS2339 - Property 'journey_lifecycle_goal' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
            journey_lifecycle_goal = [],
            // @ts-expect-error - TS2339 - Property 'question_types' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
            question_types = [],
            // @ts-expect-error - TS2339 - Property 'channels' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
            channels = [],
            // @ts-expect-error - TS2339 - Property 'from_address' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
            from_address = [],
            // @ts-expect-error - TS2339 - Property 'entity_data_filter' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
            entity_data_filter = [{id: generateUUID()}],
            // @ts-expect-error - TS2339 - Property 'question_data_filter' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
            question_data_filter = [{id: generateUUID()}],
            // @ts-expect-error - TS2339 - Property 'selected_date_range' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
            selected_date_range = {
              startDateUTC: null,
              startDate: null,
              endDateUTC: null,
              endDate: null,
            },
          } = filters;
          await dispatch(setSurveyDrilldownDashboardId(id));
          await dispatch(
            setSelectedEntitiesFiltersSurveyDrilldown(entity_data_filter),
          );
          await dispatch(
            setSelectedQuestionsFilterSurveyDrilldown(question_data_filter),
          );
          await dispatch(
            setSelectedJourneyFiltersSurveyDrilldown({
              journeys,
              journey_status,
              journey_category,
              journey_business_goal,
              journey_lifecycle_goal,
              question_types,
              channels,
              from_address,
            }),
          );
          await dispatch(setDateRangeDrilldown(selected_date_range));
        }
      };

      if (isNavigationDown === null) {
        loadSavedFilters();
        return;
      }

      if (isNavigationDown) {
        const isEntityFiltersChanged = !isEqual(
          transformEntities(entityFilters),
          transformEntities(parentEntitiesFilters),
        );

        const isJourneyFiltersChanged = !isEqual(
          journeyFilters,
          parentJourneyFilters,
        );

        const isDateRangeChanged = !isEqual(
          selected_date_range,
          parentDateRange,
        );

        if (
          isEntityFiltersChanged ||
          isJourneyFiltersChanged ||
          isDateRangeChanged
        ) {
          await dispatch(
            setSelectedEntitiesFiltersSurveyDrilldown(parentEntitiesFilters),
          );
          await dispatch(
            setSelectedJourneyFiltersSurveyDrilldown(parentJourneyFilters),
          );
          await dispatch(setDateRangeDrilldown(parentDateRange));
        } else {
          loadSavedFilters();
        }
        return;
      }
    } catch (error: any) {
      console.error('Error fetching saved dashboard filter:', error);
    } finally {
      dispatch(setNavigationDown(false));
      dispatch(setNavigationUp(false));
    }
  };

export const getCandidateNPSTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getCandidateNPSTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedSurveyNpsFilterColumnsData = await dispatch(
        reduxApi.get(
          '/candidate-nps/column-selection',
          npsCandidateTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receiveCandidateNPSTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching candidate nps table column filter:', error);
    }
  });

export const getClientNPSTableFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getClientNPSTableFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: SelectedSurveyNpsFilterColumnsData = await dispatch(
      reduxApi.get(
        '/client-nps/column-selection',
        npsClientTableColumnsQuery,
        analyticsOptions,
      ),
    );
    dispatch(receiveClientNPSTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching client nps column filter:', error);
  }
});

export const saveCandidateNPSSelectedFilterColumns: (
  columnData: SelectedSurveyNpsFilterColumnsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/candidate-nps/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);

export const getAllSurveysTableFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getAllSurveysTableFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    //TODO(Vivek):  need to change for SUrveys
    const payload: SelectedSurveyNpsFilterColumnsData = await dispatch(
      reduxApi.get(
        '/all-surveys/column-selection',
        allSurveysTableColumnsQuery,
        analyticsOptions,
      ),
    );
    dispatch(receiveAllSurveysTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching all surveys column filter:', error);
  }
});

export const saveAllSurveysFilterColumns: (
  columnData: SelectedSurveyNpsFilterColumnsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      //TODO(Vivek):  need to change for SUrveys
      reduxApi.post(
        '/all-surveys/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);

export const getConversionSummaryTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getConversionSummaryTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedSurveyNpsFilterColumnsData = await dispatch(
        reduxApi.get(
          '/all-surveys/column-selection',
          conversionSummaryTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receiveConversionSummaryTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching all surveys column filter:', error);
    }
  });

export const saveConversionSummaryTableFilterColumns: (
  columnData: SelectedSurveyNpsFilterColumnsData,
) => ThunkAction<unknown> = flow(
  key(
    (columnData) =>
      `saveConversionSummaryTableFilterColumns-${JSON.stringify(columnData)}`,
  ),
  fetching(),
)(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/all-surveys/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);
export const saveClientNPSSelectedFilterColumns: (
  columnData: SelectedSurveyNpsFilterColumnsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/client-nps/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);

export const getCandidateNPSChartFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getCandidateNPSChartFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: {
        views: Array<string> | null;
      } = await dispatch(
        reduxApi.get(
          '/candidate-nps/save-plot-view',
          npsCandidateChartColumnsQuery,
          analyticsOptions,
        ),
      );

      const uniqueViews = Array.from(new Set(payload.views ?? []));

      dispatch(
        receiveCandidateNPSChartColumnSelection(uniqueViews.slice(0, 10)),
      );
    } catch (error: any) {
      console.error('Error fetching candidate nps chart filter:', error);
    }
  });

export const getClientNPSChartFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getClientNPSChartFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: {
      views: Array<string> | null;
    } = await dispatch(
      reduxApi.get(
        '/client-nps/save-plot-view',
        npsClientChartColumnsQuery,
        analyticsOptions,
      ),
    );

    const uniqueViews = Array.from(new Set(payload.views ?? []));

    // @ts-expect-error - TS2769 - No overload matches this call.
    dispatch(receiveClientNPSChartColumnSelection(uniqueViews.slice(0, 10)));
  } catch (error: any) {
    console.error('Error fetching candidate nps chart filter:', error);
  }
});

export const saveCandidateNPSSelectedChartFilter: (
  columnData: SelectedNpsChartFilterOptionsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) => {
    const {dashboard, product, sub_dashboard, views} = columnData;
    return dispatch(
      reduxApi.post(
        '/candidate-nps/save-plot-view',
        {dashboard, product, sub_dashboard, views},
        {},
        analyticsApiOptions,
      ),
    );
  },
);

export const saveClientNPSSelectedChartFilter: (
  columnData: SelectedNpsChartFilterOptionsData,
) => ThunkAction<unknown> = flow(
  key(
    (columnData) =>
      `saveClientNPSSelectedChartFilter-${JSON.stringify(columnData)}`,
  ),
  fetching(),
)((columnData) => async (dispatch: Dispatch) => {
  const {dashboard, product, sub_dashboard, views} = columnData;
  return dispatch(
    reduxApi.post(
      '/client-nps/save-plot-view',
      {dashboard, product, sub_dashboard, views},
      {},
      analyticsApiOptions,
    ),
  );
});

export const getAllResponsesTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getAllResponsesTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedSurveyNpsFilterColumnsData = await dispatch(
        reduxApi.get(
          '/all-surveys/column-selection',
          allResponsesTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receiveAllResponsesTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching all responses table column filter:', error);
    }
  });

export const saveAllResponsesTableFilterColumns: (
  columnData: SelectedSurveyNpsFilterColumnsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/all-surveys/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);

export const getSentFromOptions: (searchQuery: string) => ThunkAction<unknown> =
  flow(
    key((searchQuery) => `sentFromOptions-${searchQuery}`),
    fetching(),
  )((searchQuery) => async (dispatch: Dispatch) => {
    try {
      const {attribute_values} = await dispatch(
        reduxApi.get('typeahead/event_resources/sent_event/from_address', {
          search_string: searchQuery,
        }),
      );
      dispatch(receiveSentFromOptions(attribute_values));
    } catch (error: any) {
      console.error('Error Fetching Sent From Options:', error);
      dispatch(receiveSentFromOptions([]));
    }
  });
